import {Injectable, EventEmitter, ElementRef} from '@angular/core';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {Store} from '@ngrx/store';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {Observable, ReplaySubject} from 'rxjs';
import {
  DocumentTestData,
  ResourceSearchType
} from '@ispm/test/components/documents-tab/interfaces/document-test-data.interface';
import {IXhtmlTemplate} from '@ispm/test/interfaces/xhtml-template.interface';
import {DOCUMENTS_TAB_ACTIONS} from '@ispm/test/constants/documents-tab.constants';
import {DateHandler} from "@ispm/shared/utils/DateHandler";
import { DatePipe } from '@angular/common';
import {IDocumentTemplate} from "@ispm/test/components/documents-tab/interfaces/document-template.interface";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Injectable()
export class DocumentsTabService {
  constructor(store: Store<IgenomicsState>,
              private http: HttpInterceptorService,
              private datePipe: DatePipe,
              private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE) {
    this.store$ = store.select('tests', 'reportPreview');
  }

  private static userID: string;
  private static baseUrl: string = '/report';
  private static workflowBaseUrl: string = '/workflow';
  private static reportsAndDocumentsUrl: string = '/reportsAndDocuments';

  private store$: any;
  public documentPreviewLoadedEvent : EventEmitter<boolean> = new EventEmitter();

  public renderedReportDivChange: string;

  loadDocumentSubject = new ReplaySubject<IDocumentTemplate>(1);

  public setState(state, userID) {
    DocumentsTabService.userID = userID;
    this.store$.dispatch({
      type: DOCUMENTS_TAB_ACTIONS.SET_STATE,
      payload: {
        state,
        userID: DocumentsTabService.userID
      }
    });
  }

  public setReview(fileType: reviewType, id: string, review: string = '') {
    let action;
    switch (fileType) {
      case reviewType.finalReview:
        action = DOCUMENTS_TAB_ACTIONS.SET_FINAL;
        break;
      case reviewType.addendumReview:
        action = DOCUMENTS_TAB_ACTIONS.SET_ADDENDUM;
        break;
      default:
        return false;
    }
    this.store$.dispatch({
      type: action,
      payload: {
        id,
        review,
        userID: DocumentsTabService.userID
      }
    });
  }

  public generateXhtmlDocument(xHtmlReportTemplate: IXhtmlTemplate, documentData: DocumentTestData): Observable<any> {
    const dateTime = new Date();
    let dateString = dateTime.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric' });

    if(xHtmlReportTemplate.clientDateFormat){
      dateString = this.datePipe.transform(dateTime, xHtmlReportTemplate.clientDateFormat);
    }else{
      dateString = dateTime.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric' });
    }
    const body = {
      xHtmlReportTemplate: xHtmlReportTemplate,
      analysis: documentData.currentTest,
      testNumber: documentData.testNumber,
      firstRevisionId: documentData.firstRevisionId,
      revisionId: documentData.revisionId,
      revisionName: documentData.revisionName,
      revisionStatus: documentData.revisionStatus,
      workflowId: documentData.workflowId,
      clientDate: dateString,
      clientTime: DateHandler.getTimeStrings(dateTime).time24String
    };

    return this.http.post(`${DocumentsTabService.baseUrl}/renderDocument`, body, true)
      .map(res => {
        return res.json();
      })
      .catch(err => {
        this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_GENERATE_DOCUMENT);
        return Observable.throw(err);
      });
  }

  public getAllTestDocuments(revisionId: string): any {
    return this.http.get(`${DocumentsTabService.reportsAndDocumentsUrl}`, { context: revisionId});
  }

  public releaseAddendumReport(userId: string, xHtmlReportTemplate: IXhtmlTemplate, testData: DocumentTestData, addendum: any) {
    const request = {
      userId: userId,
      xHtmlReportTemplate: xHtmlReportTemplate,
      analysis: testData.currentTest,
      testNumber: testData.testNumber,
      firstRevisionId: testData.firstRevisionId,
      revisionId: testData.revisionId,
      revisionName: testData.revisionName,
      revisionStatus: testData.revisionStatus,
      workflowId: testData.workflowId,
      updateText: addendum ? addendum.updateText : undefined,
      clientDate: addendum ? addendum.clientDate : undefined,
      reportTemplatesExist: addendum ? addendum.reportTemplatesExist : undefined
    };

    return this.http.post(`${DocumentsTabService.baseUrl}/addendum`, request)
      .map(res => {
        return res.json();
      })
      .catch(err => {
        this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_GENERATE_DOCUMENT);
        return Observable.throw(err);
      });
  }

  public releaseFinalReport(userId: string, xHtmlReportTemplate: IXhtmlTemplate, testData: DocumentTestData, interpretationText) {
    const request = {
      userId: userId,
      xHtmlReportTemplate: xHtmlReportTemplate,
      analysis: testData.currentTest,
      testNumber: testData.testNumber,
      firstRevisionId: testData.firstRevisionId,
      revisionId: testData.revisionId,
      revisionName: testData.revisionName,
      revisionStatus: testData.revisionStatus,
      workflowId: testData.workflowId,
      interpretationText: interpretationText != null && interpretationText != '' ? interpretationText : undefined
    };

    return this.http.post(`${DocumentsTabService.baseUrl}/release`, request)
      .map(res => {
        return res.json();
      })
      .catch(err => {
        this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_GENERATE_DOCUMENT);
        return Observable.throw(err);
      });
  }

  public generateFinalReport(userId: string, xHtmlReportTemplate: IXhtmlTemplate, testData: DocumentTestData, interpretationText) {
    const date = new Date();
    const timeStrings = DateHandler.getTimeStrings(date);

    let clientDate ;
    if(xHtmlReportTemplate.clientDateFormat){
      clientDate = this.datePipe.transform(date, xHtmlReportTemplate.clientDateFormat);
    }else{
      clientDate = date.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric' });
    }
    const renderedReportHTMLBase64 = new TextEncoder().encode(this.renderedReportDivChange);
    const binaryString = Array.from(renderedReportHTMLBase64).map(byte => String.fromCharCode(byte)).join('');

    const request = {
      userId: userId,
      xHtmlReportTemplate: xHtmlReportTemplate,
      analysis: testData.currentTest,
      testNumber: testData.testNumber,
      firstRevisionId: testData.firstRevisionId,
      revisionId: testData.revisionId,
      revisionName: testData.revisionName,
      revisionStatus: testData.revisionStatus,
      workflowId: testData.workflowId,
      clientDate: clientDate,
      reference : xHtmlReportTemplate.reference,
      clientTime : timeStrings.time12String,
      clientTime24Hours : timeStrings.time24String,
      interpretationText: interpretationText != null && interpretationText != '' ? interpretationText : undefined,
      ...(null != this.renderedReportDivChange ? { UIHtmlContent: btoa(binaryString) } : {})
    };

    return this.http.post(`${DocumentsTabService.baseUrl}/generate`, request)
      .map(res => {
        return res.ok;
      })
      .catch(err => {
        this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_GENERATE_DOCUMENT);
        return Observable.throw(err);
      });
  }


  public postReportToEmr(workflowId: string, revisionId: string) {
    let data = {
      workflowId: workflowId,
      revisionId: revisionId,
    };

    return this.http.post(`${DocumentsTabService.baseUrl}/sendToEmr`, data)
      .map(res => {
        return res.json();
      })
      .catch((err) => {
        this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_SEND_REPORT_TO_EMR);
        return Observable.throw(err);
      });
  }

  analyseRelatedControlTestStatus(data, controlSampleStatuses) {
    if (controlSampleStatuses) {
      return data.orders && data.orders.length > 0 ? data.orders.filter(order => order.resultStatus && order.resultStatus !== controlSampleStatuses[0].display).length > 0 : false;
    }
    return false;
  }
  public findResource(
    relevantResourceName: string,
    searchType: ResourceSearchType,
    testData: DocumentTestData,
    toggleLoader: boolean = true
  ): Observable<any> {
    let request;
    switch (searchType) {
      case ResourceSearchType.BY_CONTEXT:
      case ResourceSearchType.BY_ID:
        const params = searchType === ResourceSearchType.BY_ID ? {id: testData.resourceId} : {context: testData.revisionId};
        const headers = {auth_org_id: testData.currentTest.authorization[0].reference};

        request = this.http.get(`${relevantResourceName}`, params, toggleLoader, headers)
          .map(data => data.json())
          .map(item => {
            if (!item || item.length == 0) {
              this.notificationMessageService.error(`${this.notificationMessage.RESOURCE_NOT_FOUND} ${relevantResourceName}`);
            }
            return item && item.length > 0 ? item[0] : undefined;
          })
          .publish();
        break;
      case ResourceSearchType.BY_WORKFLOW_REQUEST:
      default:
        request = this.http.get(`${relevantResourceName}`, {workflowRequest: testData.workflowId}, toggleLoader)
          .map(data => data.json())
          .map(item => {
            if (!item || item.length == 0) {
              this.notificationMessageService.error(`${this.notificationMessage.RESOURCE_NOT_FOUND} ${relevantResourceName}`);
            }
            return item && item.length > 0 ? item[0] : undefined;
          })
          .publish();
        break;

    }
    request.connect();
    return request;
  }

  public findResourceByType(relevantResourceName: string, workflowId: string): Observable<any> {
    const request = this.http.get(`${relevantResourceName}`, { workflowRequest: workflowId})
      .map(data => data.json())
      .map(item => {
        if (!item || item.length == 0) {
          this.notificationMessageService.error(`${this.notificationMessage.RESOURCE_NOT_FOUND} ${relevantResourceName}`);
        }
        return item && item.length > 0 ? item[0] : undefined;
      })
      .publish();
    request.connect();
    return request;
  }

  public updateResource(relevantResourceName: string, updatedData: any, testData: DocumentTestData, toggleLoader: boolean = false): Observable<any> {
    const headers = { 'auth_org_id': testData.currentTest.authorization[0].reference };
    const request = this.http.put(`${relevantResourceName}`, updatedData, toggleLoader, headers)
      .map(res => res.body)
      .catch(err => {
        this.notificationMessageService.error(err.data
          ? err.data
          : `${this.notificationMessage.ERROR_WHILE_UPDATING} ${relevantResourceName}`
        );

        return Observable.of(null);
      })
      .publish();
    request.connect();
    return request;
  }

  public initReview(id) {
      this.store$.dispatch({
        type: DOCUMENTS_TAB_ACTIONS.INIT_REVIEWS,
        payload: {
          id,
          userID: DocumentsTabService.userID
        }
      });
    }

  public setDocumentPreviewStatusAsLoaded() {
    this.documentPreviewLoadedEvent.emit(true);
  }

  public setRenderedReportDivChange(change:string){
    this.renderedReportDivChange = change;
  }
}

export enum reviewType {
  finalReview,
  addendumReview
}
